/**
 * Recently Observed Sellers — discovery content for the Storefront Hunter
 * empty state. Backed by persisted aggregate observation data only (never a
 * provider call); it is discovery, distinct from Seller Watch (monitoring).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RatingStar } from "@/components/common/primitives";
import { addWatchSeller, fetchRecentSellers } from "@/lib/sellerWatch/api";

function errorCode(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") return detail.code || null;
  return typeof detail === "string" ? detail : null;
}

function lastObservedLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  const mins = Math.max(0, Math.round((Date.now() - d) / 60000));
  if (mins < 1) return { primary: "Just now", secondary: "Observed just now" };
  if (mins < 60) return { primary: `${mins} min ago`, secondary: `Observed ${mins} min ago` };
  const hours = Math.round(mins / 60);
  if (hours < 24) return { primary: `${hours} hr ago`, secondary: `Observed ${hours} hr ago` };
  const days = Math.round(hours / 24);
  if (days === 1) return { primary: "Yesterday", secondary: "Observed yesterday" };
  return { primary: `${days} days ago`, secondary: `Observed ${days} days ago` };
}

function ActivitySparkline({ history }) {
  if (!history || history.length === 0) return null;
  const counts = history.map((h) => Number(h.count) || 0);
  const max = Math.max(1, ...counts);
  return (
    <span
      className="sfh-rs-spark"
      role="img"
      aria-label={`${counts.reduce((a, b) => a + b, 0)} new products observed`}
    >
      {counts.map((c, i) => (
        <i
          key={i}
          className="sfh-rs-spark-bar"
          style={{ height: `${Math.max(20, Math.round((c / max) * 100))}%` }}
        />
      ))}
    </span>
  );
}

function SellerRow({ seller, onAnalyze, onTracked }) {
  const [tracked, setTracked] = useState(!!seller.is_tracked);
  const [tracking, setTracking] = useState(false);
  const observed = lastObservedLabel(seller.last_observed_at);
  const newCount = Number(seller.new_products_count) || 0;
  const rating = seller.rating != null ? Number(seller.rating) : null;

  const track = async () => {
    if (tracking || tracked) return;
    setTracking(true);
    try {
      await addWatchSeller(seller.seller_id);
      setTracked(true);
      onTracked?.();
      toast.success(`${seller.seller_name || seller.seller_id} added to Seller Watch`);
    } catch (err) {
      const code = errorCode(err);
      if (code === "already_saved") setTracked(true);
      else if (code === "seller_watch_limit_reached") toast.error("Seller Watch limit reached");
      else toast.error("Couldn't track seller. Try again.");
    } finally {
      setTracking(false);
    }
  };

  return (
    <div className="sfh-rs-row" data-testid={`sfh-recent-seller-${seller.seller_id}`}>
      <div className="sfh-rs-cell sfh-rs-seller">
        <div className="sfh-rs-name" title={seller.seller_name || undefined}>
          {seller.seller_name || `Seller ${seller.seller_id}`}
        </div>
        <div className="sfh-rs-id">Seller ID {seller.seller_id}</div>
      </div>

      <div className="sfh-rs-cell sfh-rs-num" data-testid="sfh-rs-products">
        {seller.product_count != null ? Number(seller.product_count).toLocaleString() : "—"}
      </div>

      <div className="sfh-rs-cell sfh-rs-rating">
        {rating != null ? (
          <span className="inline-flex items-center gap-0.5">
            <RatingStar />
            <span className="sfh-rs-rating-value">{rating.toFixed(1)}</span>
            {seller.review_count != null ? (
              <span className="sfh-rs-reviews">({Number(seller.review_count).toLocaleString()})</span>
            ) : null}
          </span>
        ) : (
          "—"
        )}
      </div>

      <div className="sfh-rs-cell sfh-rs-activity">
        {newCount > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="sfh-rs-new">+{newCount} new</span>
            <ActivitySparkline history={seller.activity_history} />
          </span>
        ) : (
          <span className="sfh-rs-muted">—</span>
        )}
      </div>

      <div className="sfh-rs-cell sfh-rs-observed">
        {observed ? (
          <span title={observed.secondary}>
            <span className="sfh-rs-observed-primary">{observed.primary}</span>
            <span className="sfh-rs-observed-secondary">{observed.secondary}</span>
          </span>
        ) : (
          "—"
        )}
      </div>

      <div className="sfh-rs-cell sfh-rs-actions">
        <button
          type="button"
          className="sfh-rs-analyze"
          onClick={() => onAnalyze(seller)}
          data-testid={`sfh-rs-analyze-${seller.seller_id}`}
        >
          Analyze
        </button>
        <button
          type="button"
          className={`sfh-rs-track${tracked ? " tracked" : ""}`}
          onClick={track}
          disabled={tracking || tracked}
          data-testid={`sfh-rs-track-${seller.seller_id}`}
          aria-pressed={tracked}
        >
          {tracking ? "Adding…" : tracked ? "✓ Tracking" : "+ Track"}
        </button>
      </div>
    </div>
  );
}

export function RecentlyObservedSellers({ onAnalyze, onTracked }) {
  const [state, setState] = useState({ status: "loading", sellers: [] });

  useEffect(() => {
    let cancelled = false;
    fetchRecentSellers()
      .then((data) => {
        if (cancelled) return;
        setState({ status: "ready", sellers: data?.sellers || [] });
      })
      .catch(() => {
        // Fail-soft: discovery content only; the search bar must keep working.
        if (!cancelled) setState({ status: "error", sellers: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sellers = useMemo(() => (state.sellers || []).slice(0, 5), [state.sellers]);

  // Hide gracefully when there are no recent observations or the fetch failed.
  if (state.status === "error") return null;
  if (state.status === "loading") {
    return (
      <section className="sfh-rs" data-testid="sfh-recent-sellers-loading" aria-busy="true">
        <div className="sfh-rs-head">
          <div>
            <h2 className="sfh-rs-title">Recently Observed Sellers</h2>
            <p className="sfh-rs-sub">Explore Walmart sellers recently observed by Ezisell.</p>
          </div>
        </div>
        <div className="sfh-rs-skeleton-row" />
        <div className="sfh-rs-skeleton-row" />
        <div className="sfh-rs-skeleton-row" />
      </section>
    );
  }
  if (!sellers.length) return null;

  return (
    <section className="sfh-rs" data-testid="sfh-recent-sellers">
      <div className="sfh-rs-head">
        <div>
          <h2 className="sfh-rs-title">Recently Observed Sellers</h2>
          <p className="sfh-rs-sub">Explore Walmart sellers recently observed by Ezisell.</p>
        </div>
      </div>
      <p className="sfh-rs-note">
        These sellers are not added to Seller Watch unless you choose to track them.
      </p>
      <div className="sfh-rs-table" role="table" aria-label="Recently observed sellers">
        <div className="sfh-rs-header" role="row">
          <div role="columnheader" className="sfh-rs-cell sfh-rs-seller">Seller</div>
          <div role="columnheader" className="sfh-rs-cell sfh-rs-num">Products</div>
          <div role="columnheader" className="sfh-rs-cell sfh-rs-rating">Rating</div>
          <div role="columnheader" className="sfh-rs-cell sfh-rs-activity">Recent Activity</div>
          <div role="columnheader" className="sfh-rs-cell sfh-rs-observed">Last Observed</div>
          <div role="columnheader" className="sfh-rs-cell sfh-rs-actions">Actions</div>
        </div>
        {sellers.map((s) => (
          <SellerRow key={s.seller_id} seller={s} onAnalyze={onAnalyze} onTracked={onTracked} />
        ))}
      </div>
    </section>
  );
}
